// Bas-processen: statisk struktur enligt kundens dokumentnumrering (00–32)

export type ProcessPhase = "planering" | "projektering" | "utforande" | "avslut";

export interface ProcessStepDef {
  docNumber: string;
  title: string;
  phase: ProcessPhase;
  description: string;
  /** Vilken roll som äger steget */
  owner: "Byggherre" | "Bas-P" | "Bas-U" | "Projektör" | "Bas-P/Bas-U";
  /** Referens i appen, används för "Öppna"-genväg */
  appLink?: "riskbedomning" | "amp" | "arbetsberedning" | "bruksskede" | "utforande" | "samordning" | "arbetsomrade" | "skyddsregler" | "skedesplan" | "parallell" | "overlamning" | "qalogg" | "tillbud" | "samrutin" | "bruksuppfoljning" | "skyddsrond" | "projektorinlamning" | "tidplan" | "tredjeman" | "farligtmaterial" | "elsakerhet" | "introduktion" | "basurutin" | "basuchecklista" | "skyddsverksamhet" | "platsnarvaro" | "konstruktioner" | "toolbox" | "disciplin" | "byggherrehantering" | "delegeringsordning" | "delegeringsbrev" | "byggherrechecklista" | "uppgiftsfordelning";
  /** Steget hanteras utanför systemet och kvitteras endast som utfört */
  externallyManaged?: boolean;
  afs?: string[];
  /**
   * "rutin" = styrande underlag som beslutas en gång och styr arbetet.
   * "dokument" = handling som fylls i per projekt/tillfälle. Standard: dokument.
   */
  kind?: "rutin" | "dokument";
}

/** Rutin eller ifyllnadsdokument – används för gruppering i processguiden. */
export const stepKind = (step: ProcessStepDef): "rutin" | "dokument" =>
  step.kind ?? "dokument";


export const PROCESS_PHASES: { key: ProcessPhase; label: string; description: string }[] = [
  { key: "planering", label: "Planering", description: "Uppstart, roller och förutsättningar" },
  { key: "projektering", label: "Projektering (Bas-P)", description: "Riskidentifiering, AMP och underlag" },
  { key: "utforande", label: "Utförande (Bas-U)", description: "Överlämning, uppföljning och samordning" },
  { key: "avslut", label: "Avslut & bruksskede", description: "Kvarvarande risker och drift/underhåll" },
];

export const PROCESS_STEPS: ProcessStepDef[] = [
  // 00–09 Planering
  { docNumber: "00", title: "Processöversikt Bas-P", phase: "planering", owner: "Bas-P", description: "Översikt över hela arbetsmiljöprocessen och dokumentstruktur.", afs: ["AFS 2023:3"], kind: "rutin" },
  { docNumber: "01", title: "Rutin – Systematiskt arbetsmiljöarbete (Bas-P)", phase: "planering", owner: "Bas-P", description: "Överordnad rutin för hur arbetsmiljöarbetet bedrivs systematiskt under planering och projektering.", appLink: "samrutin", afs: ["AFS 2023:1", "AFS 2023:3"], kind: "rutin" },
  { docNumber: "05", title: "Uppdragsbekräftelse – utsedd Bas-P och Bas-U", phase: "planering", owner: "Byggherre", description: "Byggherren utser Bas-P och Bas-U skriftligt, bekräftar uppdragets omfattning och kontrollerar kompetensen.", appLink: "overlamning", afs: ["AFS 2023:3 kap. 2"] },
  { docNumber: "02", title: "Funktionskrav bruksskede", phase: "planering", owner: "Bas-P", description: "Checklista för framtida drift, underhåll och bruk per utrymme.", appLink: "bruksskede", afs: ["AFS 2023:3", "AFS 2023:11"] },
  { docNumber: "03", title: "Projektförutsättningar och SKR-profil", phase: "planering", owner: "Bas-P", description: "Storlek, komplexitet och riskprofil som styr insatsnivån." },
  { docNumber: "04", title: "Förhandsanmälan Arbetsmiljöverket", phase: "planering", owner: "Byggherre", description: "Anmälan när arbetet överstiger 30 arbetsdagar/20 personer eller 500 persondagar. Hanteras externt och kvitteras här.", externallyManaged: true, afs: ["AFS 2023:3 kap. 3"] },
  { docNumber: "06", title: "Tidplansgranskning", phase: "planering", owner: "Bas-P", description: "Granskning av tidplanen: är tiderna realistiska och vilka arbeten krockar i tid och rum?", appLink: "tidplan", afs: ["AFS 2023:3 kap. 4"] },
  { docNumber: "07", title: "Tredje man och pågående verksamhet", phase: "planering", owner: "Bas-P", description: "Boende, personal och besökare som påverkas: evakuering, tillfälliga installationer, arbetstider och information.", appLink: "tredjeman", afs: ["AFS 2023:3", "AFS 2023:11"] },



  { docNumber: "08", title: "Farligt material – inventering och sanering", phase: "planering", owner: "Bas-P", description: "Inventering av asbest, PCB och andra hälsofarliga material, med provsvar, sanering och avfallsdokumentation.", appLink: "farligtmaterial", afs: ["AFS 2023:10", "AFS 2023:11"] },
  { docNumber: "09", title: "Frånkoppling och säkert avställt läge", phase: "utforande", owner: "Bas-U", description: "Frånkoppling av el, tryck och gas (LOTO) med kontroll av spänningslöshet innan arbete påbörjas.", appLink: "elsakerhet", afs: ["ELSÄK-FS 2022:1", "AFS 2023:11"] },

  // 10–19 Projektering (Bas-P)
  { docNumber: "10", title: "Arbetsområdesutredning", phase: "projektering", owner: "Bas-P", description: "Utredning av arbetsområdet: avgränsning, angränsande verksamhet, installationer och omgivning.", appLink: "arbetsomrade", afs: ["AFS 2023:3", "AFS 2023:12"] },
  { docNumber: "11", title: "Risksammanställning projektering", phase: "projektering", owner: "Bas-P", description: "Identifierade risker i byggskedet, med organisatoriska och tekniska åtgärder.", appLink: "riskbedomning", afs: ["AFS 2023:3", "AFS 2023:12"] },
  { docNumber: "12", title: "Arbetsmiljöplan (AMP)", phase: "projektering", owner: "Bas-P", description: "Upprättad AMP med särskilda risker, ordningsregler och organisation.", appLink: "amp", afs: ["AFS 2023:3 kap. 4"] },
  { docNumber: "13", title: "Särskilda arbetsmiljörisker", phase: "projektering", owner: "Bas-P", description: "Bedömning av de 13 särskilda riskerna och vilka som förekommer i projektet.", appLink: "riskbedomning", afs: ["AFS 2023:3 bilaga"] },
  { docNumber: "15", title: "Skydds- och ordningsregler", phase: "projektering", owner: "Bas-P", description: "Gemensamma skydds- och ordningsregler för arbetsplatsen. Skrivs generiskt och kan göras projektspecifika.", appLink: "skyddsregler", afs: ["AFS 2023:3 kap. 4"], kind: "rutin" },
  { docNumber: "15c", title: "Säkerhetsintroduktion per person", phase: "utforande", owner: "Bas-U", description: "Register över varje person som fått introduktion till arbetsplatsens arbetsmiljöplan och ordningsregler: datum, språk, vem som höll den och vad den omfattade.", appLink: "introduktion", afs: ["AFS 2023:3 kap. 4", "AFS 2023:1"] },
  { docNumber: "16", title: "Projektörsinlämning – kvarvarande risker", phase: "projektering", owner: "Projektör", description: "Varje projektör redovisar per disciplin de risker som inte gick att projektera bort, för byggskede och bruksskede, samt förutsättningar. Bas-P kvitterar och för in dem i risksammanställningen och AMP.", appLink: "projektorinlamning", afs: ["AFS 2023:3 kap. 2", "AFS 2023:12"] },
  { docNumber: "14", title: "Granskning och beslut projektering", phase: "projektering", owner: "Bas-P", description: "Granskning av handlingar innan överlämning till utförandeskedet. Hanteras externt och kvitteras här.", externallyManaged: true },

  // 20–29 Utförande (Bas-U)
  { docNumber: "20", title: "Överlämning Bas-P → Bas-U", phase: "utforande", owner: "Bas-P/Bas-U", description: "Formell överlämning av AMP, risksammanställning och kvarvarande frågor.", appLink: "overlamning", afs: ["AFS 2023:3"] },
  { docNumber: "21", title: "Uppföljning av utförandet", phase: "utforande", owner: "Bas-U", description: "Kontroll på plats att projekterade åtgärder faktiskt genomförs.", appLink: "utforande", afs: ["AFS 2023:3", "AFS 2023:1"] },
  { docNumber: "22", title: "Överlämning till byggherre", phase: "utforande", owner: "Bas-U", description: "Formell överlämning av kvarstående bruksrisker, drift- och underhållsunderlag till byggherren/förvaltaren.", appLink: "overlamning", afs: ["AFS 2023:3", "AFS 2023:11"] },
  { docNumber: "23", title: "Samordningsmöte Bas-P / Bas-U", phase: "utforande", owner: "Bas-P/Bas-U", description: "Protokollförda möten om risker, ändringar och kvarstående punkter.", appLink: "samordning" },
  { docNumber: "25", title: "Q&A-logg", phase: "utforande", owner: "Bas-P/Bas-U", description: "Logg över frågor och svar i projektet: vem frågade, vem svarar, när och status.", appLink: "qalogg" },
  { docNumber: "24", title: "Arbetsberedningar", phase: "utforande", owner: "Bas-U", description: "Momentvisa beredningar kopplade till identifierade risker.", appLink: "arbetsberedning" },
  { docNumber: "28", title: "Tillbud- och olycksrapportering", phase: "utforande", owner: "Bas-U", description: "Rapportering av tillbud, olyckor och allvarliga händelser med utredning, åtgärder och AMV-anmälan.", appLink: "tillbud", afs: ["AFS 2023:1"] },
  { docNumber: "27", title: "Skyddsrondsprotokoll", phase: "utforande", owner: "Bas-U", description: "Protokollförda skyddsronder på arbetsplatsen, inklusive den överlämnande skyddsronden inför bruksskedet.", appLink: "skyddsrond", afs: ["AFS 2023:1", "AFS 2023:3"] },

  // 30–32 Avslut & bruksskede
  { docNumber: "26", title: "Skedesplan och APD-plan", phase: "utforande", owner: "Bas-U", description: "Skeden i tid och rum, samtidiga arbeten och gemensamma skyddsanordningar.", appLink: "skedesplan", afs: ["AFS 2023:3"] },

  { docNumber: "30", title: "Slutkontroll arbetsmiljö", phase: "avslut", owner: "Bas-U", description: "Avstämning att alla risker är hanterade eller överförda. Hanteras externt och kvitteras här.", externallyManaged: true },
  { docNumber: "31", title: "Kvarvarande bruksrisker", phase: "avslut", owner: "Bas-P", description: "Risker som kvarstår i bruksskedet och hur de ska hanteras av förvaltaren.", appLink: "bruksskede", afs: ["AFS 2023:11"] },
  { docNumber: "33", title: "Uppföljning bruksskede – arbetsplatsens utformning", phase: "avslut", owner: "Bas-P", description: "Slutlig verifiering av att funktionskraven för bruksskedet (Dok 02) är uppfyllda i det färdiga byggnadsverket. Lämnas över tillsammans med arbetsmiljödokumentationen.", appLink: "bruksuppfoljning", afs: ["AFS 2023:3", "AFS 2023:11"] },
  { docNumber: "34", title: "Överlämningssammanfattning inför bruksskede", phase: "avslut", owner: "Bas-P", description: "Bas-P:s sammanfattning av hela arbetsmiljödokumentationen inför bruksskedet, inklusive underlag till den överlämnande skyddsronden.", appLink: "overlamning", afs: ["AFS 2023:3", "AFS 2023:11"] },
  { docNumber: "32", title: "Överlämning till förvaltning", phase: "avslut", owner: "Byggherre", description: "Dokumentation för drift, underhåll och framtida arbeten." },

  // 40–46 Bas-U:s egna rutiner och kontroller
  { docNumber: "40", title: "Rutin Bas-U", phase: "utforande", owner: "Bas-U", description: "Bas-U:s arbetssätt genom hela produktionen: övertagande, planering, samordning, uppföljning och avslut.", appLink: "basurutin", afs: ["AFS 2023:3 kap. 4", "AFS 2023:13 kap. 5"], kind: "rutin" },
  { docNumber: "41", title: "Checklista Bas-U", phase: "utforande", owner: "Bas-U", description: "Kontrollpunkter från övertagande till överlämning med status, ansvarig och avvikelser.", appLink: "basuchecklista", afs: ["AFS 2023:3 kap. 4"], kind: "rutin" },
  { docNumber: "42", title: "Plan för skyddsverksamhet", phase: "utforande", owner: "Bas-U", description: "Skyddsorganisation, möten, skyddsronder, introduktion och beredskap på arbetsplatsen.", appLink: "skyddsverksamhet", afs: ["AFS 2023:1", "AFS 2023:3 kap. 4"], kind: "rutin" },
  { docNumber: "43", title: "Platsnärvaro Bas-U", phase: "utforande", owner: "Bas-U", description: "Överenskommelse med byggherren om närvaro på plats, kontaktperson och rutiner utanför ordinarie tid.", appLink: "platsnarvaro", afs: ["AFS 2023:3 kap. 2 § 17"] },
  { docNumber: "44", title: "Tillfälliga och samverkande konstruktioner", phase: "utforande", owner: "Bas-U", description: "Ställning, form, stämp och spont med kompetent person, handlingar och kontroll före användning.", appLink: "konstruktioner", afs: ["AFS 2023:13 kap. 5"] },
  { docNumber: "45", title: "Toolbox talk – arbetsmiljödialog", phase: "utforande", owner: "Bas-U", description: "Korta genomgångar av aktuella risker med deltagarlista och beslut.", appLink: "toolbox", afs: ["AFS 2023:1"] },
  { docNumber: "46", title: "Disciplinära åtgärder", phase: "utforande", owner: "Bas-U", description: "Logg över brott mot ordningsreglerna och vidtagna åtgärder – tillsägelse, varning, stoppat arbete eller avvisning.", appLink: "disciplin", afs: ["AFS 2023:3 kap. 4"] },

  // 50–54 Byggherrens organisation, delegering och egenkontroll
  { docNumber: "50", title: "Byggherrens hantering av byggarbetsmiljöarbete", phase: "planering", owner: "Byggherre", description: "Byggherrens överordnade dokument: organisation, kompetens, hur Bas-P och Bas-U utses och följs upp samt hur tillsyn och kontroll utövas.", appLink: "byggherrehantering", afs: ["AFS 2023:3 kap. 2"], kind: "rutin" },
  { docNumber: "51", title: "Delegeringsordning byggherre", phase: "planering", owner: "Byggherre", description: "Kedjan från VD ned till byggherrens representant: vem som får fördela vidare, befogenheter och ställföreträdare.", appLink: "delegeringsordning", afs: ["AFS 2023:3 kap. 2 § 15"], kind: "rutin" },
  { docNumber: "52", title: "Delegeringsbrev", phase: "planering", owner: "Byggherre", description: "Personligt brev där arbetsmiljöuppgifter och befogenheter fördelas till namngiven mottagare, med giltighet och underskrift.", appLink: "delegeringsbrev", afs: ["AFS 2023:3 kap. 2", "AFS 2023:11"] },
  { docNumber: "53", title: "Checklista lagefterlevnad byggherre", phase: "planering", owner: "Byggherre", description: "Byggherrens egen tillsyn och kontroll i fyra skeden: planering, projektering, utförande och slutdokumentation.", appLink: "byggherrechecklista", afs: ["AFS 2023:3 kap. 2"], kind: "rutin" },
  { docNumber: "54", title: "Fördelning av arbetsmiljöuppgifter", phase: "planering", owner: "Byggherre", description: "Matris över ansvarig, handläggare och informerad per arbetsmiljöuppgift – särskilt i underhålls- och avhjälpandeprojekt.", appLink: "uppgiftsfordelning", afs: ["AFS 2023:3 kap. 2"] },

  // Kompletterande dokument som tidigare saknades i processguiden
  { docNumber: "00c", title: "Processöversikt – läsbar sammanfattning", phase: "planering", owner: "Bas-P", description: "Sammanfattande rapport över projektets processläge, skeden och dokument. Används som lägesbild internt och mot byggherren.", kind: "rutin" },
  { docNumber: "17", title: "Arbetsmiljökrav i upphandling", phase: "planering", owner: "Byggherre", description: "Krav på arbetsmiljö, kompetens och samordning som ska in i förfrågningsunderlag och avtal med entreprenörer och projektörer.", kind: "rutin", afs: ["AFS 2023:3 kap. 2"] },
  { docNumber: "18", title: "Projektörens arbetsmiljörutin", phase: "projektering", owner: "Projektör", description: "Hur projektören arbetar systematiskt med arbetsmiljö: identifiera, projektera bort och redovisa kvarvarande risker.", kind: "rutin", afs: ["AFS 2023:3 kap. 2", "AFS 2023:12"] },
  { docNumber: "19", title: "Leveransintyg projektör", phase: "projektering", owner: "Projektör", description: "Intyg med verifieringskod som visar vad projektörsleveransen innehåller och vilka kvarvarande risker som lämnas över.", afs: ["AFS 2023:3 kap. 2"] },
  { docNumber: "10b", title: "Samlad arbetsområdesutredning", phase: "projektering", owner: "Bas-P", description: "Bas-P slår ihop projektörernas och egna arbetsområdesutredningar till en samordnad bild av arbetsområdet.", appLink: "arbetsomrade", afs: ["AFS 2023:3", "AFS 2023:12"] },
  { docNumber: "30b", title: "Risksammanställning utförande", phase: "projektering", owner: "Bas-P", description: "Riskerna som gäller själva byggskedet, med åtgärder som förs vidare till AMP och arbetsberedningar.", appLink: "riskbedomning", afs: ["AFS 2023:3", "AFS 2023:12"] },
  { docNumber: "30c", title: "Samordnad risksammanställning", phase: "projektering", owner: "Bas-P", description: "Bas-P samordnar risker från flera projektörer och discipliner till en gemensam lista utan dubbletter och med lösta konflikter.", appLink: "riskbedomning", afs: ["AFS 2023:3 kap. 4"] },
  { docNumber: "06c", title: "Dagslägesrapport", phase: "utforande", owner: "Bas-U", description: "Kort lägesrapport per datum: pågående arbeten, aktuella risker, avvikelser och vad som gäller närmast.", afs: ["AFS 2023:1"] },
  { docNumber: "24b", title: "Startbesked arbetsberedning", phase: "utforande", owner: "Bas-U", description: "Skriftligt klartecken innan ett riskfyllt moment får starta – att beredning, kompetens och skydd är på plats.", appLink: "arbetsberedning", afs: ["AFS 2023:3 kap. 4"] },
  { docNumber: "29", title: "Entreprenörens kvittenser och kompetensbevis", phase: "utforande", owner: "Bas-U", description: "Entreprenörernas bekräftelser på mottagna regler och risker samt intyg på behörigheter och utbildningar.", afs: ["AFS 2023:1", "AFS 2023:3 kap. 4"] },
  { docNumber: "55", title: "Samlad byggherrerapport", phase: "avslut", owner: "Byggherre", description: "Samlad rapport över delegeringskedja, lagefterlevnad och status per skede – byggherrens bevis på utförd tillsyn.", appLink: "byggherrechecklista", afs: ["AFS 2023:3 kap. 2"] },
  { docNumber: "60", title: "Arbetsmiljöplan liten omfattning", phase: "projektering", owner: "Bas-P", description: "Samlat minidokument för små projekt: roller, arbetsområde, de 13 särskilda riskerna, övriga risker samt regler och kontakter. Skrivs till samma underlag som de fullständiga dokumenten.", afs: ["AFS 2023:3 kap. 4"] },
];



export type StepStatus = "not_started" | "in_progress" | "done" | "not_applicable";

// Klarspråk: fyra lägen som säger vad användaren ska göra, inte hur systemet mår.
export const STEP_STATUS_LABELS: Record<StepStatus, string> = {
  not_started: "Behöver din åtgärd",
  in_progress: "Pågår",
  done: "Klar",
  not_applicable: "Ej aktuell",
};

// ---------- Bruksskede: funktionskrav (Dok 02) ----------

export interface BruksskedeItemDef {
  key: string;
  controlPoint: string;
  legalReference?: string;
  explanation?: string;
}

export interface BruksskedeSectionDef {
  section: string;
  items: BruksskedeItemDef[];
}

export const BRUKSSKEDE_TEMPLATE: BruksskedeSectionDef[] = [
  {
    section: "Tillträde och åtkomst",
    items: [
      { key: "tilltrade-1", controlPoint: "Finns säker och permanent åtkomst till utrymmet för drift och underhåll?", legalReference: "AFS 2023:11", explanation: "Trappor, gångar, fasta stegar eller plattformar i stället för tillfälliga lösningar." },
      { key: "tilltrade-2", controlPoint: "Är fri höjd, passagebredd och dörrbredd tillräcklig för personal och utrustning?", legalReference: "AFS 2023:11" },
      { key: "tilltrade-3", controlPoint: "Finns fallskydd eller fästpunkter där arbete sker på höjd (tak, schakt, öppningar)?", legalReference: "AFS 2023:3" },
      { key: "tilltrade-4", controlPoint: "Kan tunga komponenter transporteras in/ut utan manuell tunglyft?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "Utrymning och brandskydd",
    items: [
      { key: "brand-1", controlPoint: "Finns minst en utrymningsväg som är fri och tydligt markerad?", legalReference: "AFS 2023:11" },
      { key: "brand-2", controlPoint: "Är brandsläckningsutrustning placerad och åtkomlig?", legalReference: "AFS 2023:11" },
      { key: "brand-3", controlPoint: "Är dörrar öppningsbara inifrån utan nyckel?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "Klimat, ljus och ventilation",
    items: [
      { key: "klimat-1", controlPoint: "Är belysningen tillräcklig för de arbetsuppgifter som utförs i utrymmet?", legalReference: "AFS 2023:11" },
      { key: "klimat-2", controlPoint: "Finns tillräcklig ventilation, även vid arbete med kemikalier eller värme?", legalReference: "AFS 2023:11" },
      { key: "klimat-3", controlPoint: "Är temperatur och buller acceptabla vid normalt underhållsarbete?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "Säkerhet och risker i drift",
    items: [
      { key: "sakerhet-1", controlPoint: "Går det att göra anläggningen strömlös/trycklös på ett säkert sätt?", legalReference: "ELSÄK-FS 2022:1" },
      { key: "sakerhet-2", controlPoint: "Finns risk för klämning, roterande delar eller heta ytor utan skydd?", legalReference: "AFS 2023:11" },
      { key: "sakerhet-3", controlPoint: "Krävs ensamarbete i utrymmet, och finns i så fall larm/kommunikation?", legalReference: "AFS 2023:1" },
      { key: "sakerhet-4", controlPoint: "Finns kvarvarande kemiska eller biologiska risker (asbest, mögel, PCB)?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    section: "Skyltar och märkning",
    items: [
      { key: "skylt-1", controlPoint: "Är utrymmet, ledningar och avstängningar tydligt märkta?", legalReference: "AFS 2023:11" },
      { key: "skylt-2", controlPoint: "Finns varningsskyltar där kvarvarande risker förekommer?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "Maskiner och installationer",
    items: [
      { key: "maskin-1", controlPoint: "Finns utrymme för service runt maskiner och aggregat?", legalReference: "AFS 2023:11" },
      { key: "maskin-2", controlPoint: "Finns lyftöglor, balk eller lyftanordning för tunga komponenter?", legalReference: "AFS 2023:11" },
      { key: "maskin-3", controlPoint: "Finns bruksanvisningar och underhållsinstruktioner överlämnade?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "Utbildning och information",
    items: [
      { key: "utb-1", controlPoint: "Krävs särskild utbildning eller behörighet för arbete i utrymmet?", legalReference: "AFS 2023:1" },
      { key: "utb-2", controlPoint: "Har förvaltaren fått information om kvarvarande risker?", legalReference: "AFS 2023:3" },
    ],
  },
];

export type BruksskedeStatus = "unanswered" | "ok" | "deviation" | "na";

export const BRUKSSKEDE_STATUS_LABELS: Record<BruksskedeStatus, string> = {
  unanswered: "Obesvarad",
  ok: "Uppfyllt",
  deviation: "Avvikelse",
  na: "Ej aktuellt",
};

// ---------- Uppföljning bruksskede (Dok 33) ----------
// Samma kontrollpunkter som funktionskraven i Dok 02, men verifierade i det
// färdiga byggnadsverket innan arbetsmiljödokumentationen lämnas över.

export const USE_PHASE_TEMPLATE: BruksskedeSectionDef[] = BRUKSSKEDE_TEMPLATE;

export type UsePhaseStatus = "unanswered" | "verified" | "deviation" | "na";

export const USE_PHASE_STATUS_LABELS: Record<UsePhaseStatus, string> = {
  unanswered: "Ej kontrollerad",
  verified: "Verifierad på plats",
  deviation: "Avvikelse",
  na: "Ej aktuellt",
};


// ---------- Utförande (Dok 21) ----------

export interface ExecutionTemplateItem {
  area: string;
  title: string;
  description?: string;
}

export const EXECUTION_TEMPLATE: ExecutionTemplateItem[] = [
  { area: "Etablering", title: "Är arbetsplatsdispositionen (APD-plan) uppsatt och aktuell?" },
  { area: "Etablering", title: "Är in- och utfarter, gångvägar och upplagsplatser utmärkta enligt projekteringen?" },
  { area: "Fallskydd", title: "Är projekterade fallskydd och räcken monterade innan arbete på höjd påbörjas?" },
  { area: "Lyft", title: "Är lyftplaner upprättade för de lyft som identifierats i projekteringen?" },
  { area: "El", title: "Är tillfällig el utförd och kontrollerad av behörig person?" },
  { area: "Schakt", title: "Är schakter släntade eller spontade enligt handling?" },
  { area: "Brand", title: "Följs rutiner för heta arbeten och brandfarlig vara?" },
  { area: "Samordning", title: "Är samordning genomförd med övriga entreprenörer på plats?" },
  { area: "Kemikalier", title: "Finns säkerhetsdatablad och kemikalieförteckning på plats?" },
  { area: "Uppföljning", title: "Är kvarvarande frågor från Bas-P besvarade och stängda?" },
];

export type ExecutionStatus = "not_checked" | "ok" | "deviation" | "na";

export const EXECUTION_STATUS_LABELS: Record<ExecutionStatus, string> = {
  not_checked: "Ej kontrollerad",
  ok: "OK",
  deviation: "Avvikelse",
  na: "Ej aktuell",
};

// ---------- Samordningsmöte (Dok 23) ----------

export const COORDINATION_AGENDA: { key: string; label: string; hint?: string }[] = [
  { key: "genomgang_amp", label: "Genomgång av AMP och särskilda risker" },
  { key: "kvarvarande_risker", label: "Kvarvarande risker från projekteringen" },
  { key: "andringar", label: "Ändringar i handlingar som påverkar arbetsmiljön" },
  { key: "samordning_ue", label: "Samordning mellan entreprenörer och samtidiga arbeten" },
  { key: "avvikelser", label: "Avvikelser och tillbud sedan förra mötet" },
  { key: "beslut", label: "Beslut och åtgärder", hint: "Vem gör vad, till när" },
];

// ---------- Arbetsområdesutredning (Dok 10) ----------

export interface WorkAreaItemDef {
  key: string;
  controlPoint: string;
  legalReference?: string;
  explanation?: string;
}

export interface WorkAreaSectionDef {
  category: string;
  items: WorkAreaItemDef[];
}

export const WORK_AREA_TEMPLATE: WorkAreaSectionDef[] = [
  {
    category: "Avgränsning och gränsdragning",
    items: [
      { key: "avgr-1", controlPoint: "Är arbetsområdets gräns fastställd och möjlig att stängsla in?", legalReference: "AFS 2023:3" },
      { key: "avgr-2", controlPoint: "Finns tillräckliga etablerings- och upplagsytor inom området?", legalReference: "AFS 2023:3" },
      { key: "avgr-3", controlPoint: "Är ansvarsgränser mot beställare och andra entreprenörer tydliga?", legalReference: "AFS 2023:3" },
    ],
  },
  {
    category: "Angränsande verksamhet och tredje man",
    items: [
      { key: "tredje-1", controlPoint: "Pågår verksamhet i eller intill arbetsområdet under byggtiden?", legalReference: "AFS 2023:3" },
      { key: "tredje-2", controlPoint: "Finns skola, boende eller allmänhet som kan påverkas av arbetet?", legalReference: "AFS 2023:3" },
      { key: "tredje-3", controlPoint: "Krävs skyddsåtgärder mot tredje man (skärmtak, avspärrning, vakt)?", legalReference: "AFS 2023:3" },
    ],
  },
  {
    category: "Transport och logistik",
    items: [
      { key: "log-1", controlPoint: "Är in- och utfarter samt transportvägar möjliga att ordna säkert?", legalReference: "AFS 2023:3" },
      { key: "log-2", controlPoint: "Är gång- och cykeltrafik separerad från byggtrafik?", legalReference: "AFS 2023:3" },
      { key: "log-3", controlPoint: "Finns utrymme för lyftzoner och uppställning av kran/mobilkran?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    category: "Befintliga installationer",
    items: [
      { key: "inst-1", controlPoint: "Finns luftledningar eller närliggande elanläggningar som påverkar arbetet?", legalReference: "ELSÄK-FS 2022:1" },
      { key: "inst-2", controlPoint: "Är kablar och ledningar i mark inmätta och utsättningar beställda?", legalReference: "AFS 2023:3" },
      { key: "inst-3", controlPoint: "Finns rör, gas eller fjärrvärme som måste säkras eller kopplas bort?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    category: "Mark och grund",
    items: [
      { key: "mark-1", controlPoint: "Är bergnivå, jordlager och schaktförhållanden kända (geoteknisk utredning)?", legalReference: "AFS 2023:3" },
      { key: "mark-2", controlPoint: "Finns grundvatten eller risk för ras/skred som kräver spont eller släntning?", legalReference: "AFS 2023:12" },
      { key: "mark-3", controlPoint: "Finns misstanke om förorenad mark inom området?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    category: "Befintlig byggnad",
    items: [
      { key: "bygg-1", controlPoint: "Finns asbest, PCB eller andra hälsofarliga material i befintlig konstruktion?", legalReference: "AFS 2023:12" },
      { key: "bygg-2", controlPoint: "Är bärighet och stabilitet utredd inför rivning eller ombyggnad?", legalReference: "AFS 2023:3" },
      { key: "bygg-3", controlPoint: "Finns relationshandlingar och tidigare arbetsmiljödokumentation tillgängliga?", legalReference: "AFS 2023:3" },
    ],
  },
  {
    category: "Omgivningspåverkan",
    items: [
      { key: "omg-1", controlPoint: "Medför arbetet buller, damm eller vibrationer som kräver särskilda åtgärder?", legalReference: "AFS 2023:11" },
      { key: "omg-2", controlPoint: "Påverkas arbetet av väder, mörker eller vinterförhållanden?", legalReference: "AFS 2023:11" },
      { key: "omg-3", controlPoint: "Krävs tillstånd eller dispenser (trafik, schakt, buller) för arbetet?", legalReference: "AFS 2023:3" },
    ],
  },
];

export type WorkAreaSurveyStatus = "not_assessed" | "no_risk" | "risk" | "na";

export const WORK_AREA_STATUS_LABELS: Record<WorkAreaSurveyStatus, string> = {
  not_assessed: "Ej bedömd",
  no_risk: "Ingen risk",
  risk: "Risk identifierad",
  na: "Ej aktuell",
};

// ---------- Skedesplan / APD-plan (Dok 32) ----------

export const APD_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: "etablering", label: "Etablering och personalutrymmen", hint: "Bodar, omklädning, matplats, parkering" },
  { key: "transportvagar", label: "Transportvägar och angöring", hint: "In- och utfart, gångvägar, separering gående/fordon" },
  { key: "upplag", label: "Upplag och materialhantering", hint: "Lastzoner, upplagsytor, avfall" },
  { key: "avsparrningar", label: "Avspärrningar och riskområden", hint: "Skyddsområden, riskzoner, skyltning" },
  { key: "lyft", label: "Kranar, lyft och maskiner", hint: "Kranplacering, svängradier, samtidiga lyft" },
  { key: "gemensamma", label: "Gemensamma skyddsanordningar", hint: "Räcken, belysning, ställningar, brandskydd" },
];

export interface StagePlanStage {
  id: string;
  name: string;
  start?: string;
  end?: string;
  activities?: string;
  contractors?: string;
  risks?: string;
  responsible?: string;
}

// ---------- Projektledardokument: parallell projektering och produktion ----------

export const PARALLEL_SECTIONS: { key: "deliveries" | "overlaps" | "decisions"; title: string; description: string }[] = [
  {
    key: "deliveries",
    title: "Projekteringsleveranser under pågående produktion",
    description: "Handlingar som fortfarande projekteras och när produktionen behöver dem.",
  },
  {
    key: "overlaps",
    title: "Moment där projektering och byggande överlappar",
    description: "Där arbete startar innan handlingar är helt klara – risk och beslutspunkt.",
  },
  {
    key: "decisions",
    title: "Beslutslogg",
    description: "Vem beslutar, senast när och vad konsekvensen blir om beslutet dröjer.",
  },
];

export const PARALLEL_ROLES = ["Byggherre", "Projektledare", "Bas-P", "Bas-U", "Projektör"] as const;

// ---------- Överlämningsdokument (Dok 20 och Dok 22) ----------
// Strukturen följer kundens Word-mallar:
// "20 MALL Överlämning Bas-P till Bas-U" och "22 MALL Överlämning till byggherre".

export type HandoverKind =
  | "basu"
  | "byggherre"
  | "slutsammanfattning"
  | "utseende_basp"
  | "utseende_basu";

export const HANDOVER_KIND_LABELS: Record<HandoverKind, string> = {
  basu: "Dok 20 · Överlämning Bas-P till Bas-U",
  byggherre: "Dok 22 · Överlämning till byggherre",
  slutsammanfattning: "Dok 34 · Överlämningssammanfattning inför bruksskede",
  utseende_basp: "Dok 05 · Uppdragsbekräftelse och utsedd Bas-P",
  utseende_basu: "Dok 05 · Uppdragsbekräftelse och utsedd Bas-U",
};

export type HandoverFieldType = "text" | "textarea" | "date" | "checklist" | "signature";

export interface HandoverFieldDef {
  key: string;
  label: string;
  hint?: string;
  type?: HandoverFieldType;
  /** Punkter för type = "checklist" */
  items?: string[];
  /** Parter för type = "signature" */
  parties?: string[];
}

export interface HandoverSectionDef {
  key: string;
  title: string;
  /** Fast ledtext från mallen – visas i editorn och i Word-exporten */
  intro?: string[];
  /** Punktlista som hör till ledtexten */
  bullets?: string[];
  /** Avslutande ledtext efter punktlistan */
  outro?: string[];
  fields: HandoverFieldDef[];
}

/** Dokumenttitlar exakt enligt mallarna */
export const HANDOVER_DOC_TITLES: Record<HandoverKind, string> = {
  basu: "Överlämning Bas-P till Bas-U",
  byggherre:
    "Rutin för framtagande och överlämning av arbetsmiljödokumentation för bruksskedet",
  slutsammanfattning: "Överlämningssammanfattning inför bruksskede och skyddsrond",
  utseende_basp: "Uppdragsbekräftelse – byggherrens utseende av byggarbetsmiljösamordnare Bas-P",
  utseende_basu: "Uppdragsbekräftelse – byggherrens utseende av byggarbetsmiljösamordnare Bas-U",
};


/** Dokumenthuvud som finns överst i mallarna 22 och 25 */
export const DOC_HEADER_COLUMNS = ["Projektnummer", "Projektnamn", "Version", "Status"] as const;

/**
 * Dok 05: byggherrens skriftliga utseende av byggarbetsmiljösamordnare.
 * Samma struktur för Bas-P och Bas-U, med rollen inskriven i ledtexterna.
 */
const buildAppointmentSections = (role: "Bas-P" | "Bas-U"): HandoverSectionDef[] => {
  const phase = role === "Bas-P" ? "planering och projektering" : "utförandet";
  return [
    {
      key: "huvud",
      title: "Dokumenthuvud",
      fields: [
        { key: "projektnummer", label: "Projektnummer" },
        { key: "projektnamn", label: "Projektnamn" },
        { key: "version", label: "Version" },
        { key: "status", label: "Status" },
      ],
    },
    {
      key: "parter",
      title: "Parter",
      intro: [
        `Byggherren ska skriftligt utse en byggarbetsmiljösamordnare för ${phase} (${role}) och försäkra sig om att den utsedde har den utbildning, kompetens och erfarenhet som uppdraget kräver.`,
      ],
      fields: [
        { key: "byggherre", label: "Byggherre (organisation)" },
        { key: "byggherre_kontakt", label: "Byggherrens kontaktperson" },
        { key: "objekt", label: "Objekt / byggnadsverk" },
        { key: "samordnare", label: `Utsedd ${role} (namn)` },
        { key: "samordnare_foretag", label: "Företag / organisation" },
        { key: "samordnare_kontakt", label: "E-post och telefon" },
        { key: "startdatum", label: "Uppdraget gäller från", type: "date" },
        { key: "slutdatum", label: "Uppdraget gäller till", type: "date" },
      ],
    },
    {
      key: "omfattning",
      title: "Uppdragets omfattning",
      intro: [`Beskriv vilka uppgifter som ingår i uppdraget som ${role} och vad som uttryckligen inte ingår.`],
      fields: [
        { key: "uppgifter", label: "Uppgifter som ingår", type: "textarea" },
        { key: "avgransning", label: "Avgränsningar – vad ingår inte", type: "textarea" },
        { key: "befogenheter", label: "Befogenheter och resurser", type: "textarea" },
        { key: "arbetsmiljoansvar", label: "Fördelning av arbetsmiljöuppgifter", type: "textarea" },
      ],
    },
    {
      key: "kompetens",
      title: "Kompetens och lämplighet",
      intro: [
        "Byggherren ska kunna visa att den utsedde är lämplig för uppgiften. Ange underlag och bifoga intyg.",
      ],
      fields: [
        {
          key: "underlag",
          label: "Kontrollerat underlag",
          type: "checklist",
          items: [
            "CV och relevant yrkeserfarenhet",
            "Utbildning BAS-P/BAS-U enligt AFS 2023:3",
            "Kännedom om aktuell arbetsmiljölagstiftning",
            "Erfarenhet av liknande projekt och riskbild",
            "Tillgång till nödvändiga resurser och tid",
          ],
        },
        { key: "utbildning", label: "Utbildningar och intyg", type: "textarea" },
        { key: "erfarenhet", label: "Erfarenhet av liknande projekt", type: "textarea" },
        { key: "bilagor", label: "Bilagor (intyg, certifikat)", type: "textarea" },
      ],
    },
    {
      key: "bekraftelse",
      title: "Bekräftelse och underskrifter",
      intro: [
        `Genom underskrift bekräftar byggherren utseendet och den utsedde att uppdraget som ${role} accepteras.`,
      ],
      fields: [
        { key: "kommentar", label: "Kommentar", type: "textarea" },
        {
          key: "signaturer",
          label: "Underskrifter",
          type: "signature",
          parties: ["Byggherre", role],
        },
      ],
    },
  ];
};

export const HANDOVER_SECTIONS: Record<HandoverKind, HandoverSectionDef[]> = {
  basu: [
    {
      key: "projektinformation",
      title: "Projektinformation",
      fields: [
        { key: "projekt", label: "Projekt" },
        { key: "objekt", label: "Objekt" },
        { key: "datum", label: "Datum för överlämning", type: "date" },
        { key: "basp_handlaggare", label: "Bas-P handläggare" },
        { key: "basu_handlaggare", label: "Bas-U handläggare" },
        { key: "projektledare", label: "Projektledare" },
      ],
    },
    {
      key: "inledning",
      title: "Inledning",
      intro: [
        "Syftet med detta möte är att genomföra en strukturerad överlämning av arbetsmiljöarbetet från Bas-P (planering och projektering) till Bas-U (utförande).",
        "Överlämningen ska säkerställa att:",
      ],
      bullets: [
        "identifierade arbetsmiljörisker är kända inför byggskedet",
        "arbetsmiljöplanen är etablerad",
        "kvarstående risker och skyddsåtgärder är tydliga",
        "Bas-U får tillräckligt underlag för att leda arbetsmiljöarbetet under utförandet",
      ],
      fields: [
        {
          key: "noteringar",
          label: "Noteringar",
          hint: "Överlämningen utgör en del av projektets systematiska arbetsmiljöarbete enligt arbetsmiljölagen och Arbetsmiljöverkets föreskrifter.",
          type: "textarea",
        },
      ],
    },
    {
      key: "status_basp",
      title: "Status från Bas-P",
      intro: ["Bas-P redovisar kort status för arbetsmiljöarbetet under planering och projektering."],
      bullets: [
        "genomförd riskidentifiering i projekteringen",
        "status för risksammanställning (risklogg)",
        "status för arbetsmiljöplan",
        "eventuella kvarstående frågor eller beslut",
      ],
      fields: [
        { key: "riskidentifiering", label: "Genomförd riskidentifiering", type: "textarea" },
        { key: "risklogg", label: "Status risksammanställning (risklogg)", type: "textarea" },
        { key: "amp", label: "Status arbetsmiljöplan", type: "textarea" },
        { key: "kvarstaende", label: "Kvarstående frågor eller beslut", type: "textarea" },
        {
          key: "fortsatt_projektering",
          label: "Delar av projekteringen som fortsätter under byggskedet",
          type: "textarea",
        },
      ],
    },
    {
      key: "risker",
      title: "Genomgång av identifierade arbetsmiljörisker",
      intro: ["Bas-P går igenom projektets risksammanställning (risklogg)."],
      bullets: [
        "identifierade risker kopplade till olika arbetsmoment",
        "risker kopplade till särskilt farliga arbeten",
        "risker kopplade till gemensamma konstruktioner eller arbetsmetoder",
        "eventuella risker som kräver särskild uppföljning under utförandet",
      ],
      fields: [
        { key: "arbetsmoment", label: "Risker kopplade till arbetsmoment", type: "textarea" },
        { key: "sarskilt_farliga", label: "Särskilt farliga arbeten", type: "textarea" },
        { key: "gemensamma", label: "Gemensamma konstruktioner eller arbetsmetoder", type: "textarea" },
        { key: "uppfoljning", label: "Risker som kräver särskild uppföljning", type: "textarea" },
        {
          key: "gransdragning",
          label: "Gränsdragningar mellan projektering och utförande",
          type: "textarea",
        },
      ],
    },
    {
      key: "byggbarhet",
      title: "Byggbarhet och planerade arbetsmetoder",
      intro: [
        "Bas-P redovisar övergripande förutsättningar som påverkar arbetsmiljön under byggskedet, exempelvis:",
      ],
      bullets: [
        "trafik- och arbetsplatsdisposition (TA)",
        "etableringsförutsättningar",
        "logistik och materialhantering",
        "samordning mellan olika entreprenader",
        "eventuella särskilda arbetsmetoder",
      ],
      outro: [
        "Syftet är att säkerställa att Bas-U har förståelse för de förutsättningar som legat till grund för projekteringen.",
      ],
      fields: [
        { key: "ta_apd", label: "Trafik- och arbetsplatsdisposition (TA)", type: "textarea" },
        { key: "etablering", label: "Etableringsförutsättningar", type: "textarea" },
        { key: "logistik", label: "Logistik och materialhantering", type: "textarea" },
        { key: "samordning", label: "Samordning mellan olika entreprenader", type: "textarea" },
        { key: "metoder", label: "Särskilda arbetsmetoder", type: "textarea" },
      ],
    },
    {
      key: "dokument",
      title: "Överlämnade dokument",
      intro: ["Följande dokument överlämnas från Bas-P till Bas-U:"],
      fields: [
        {
          key: "checklista",
          label: "Överlämnade dokument",
          type: "checklist",
          items: [
            "Dokument 11 – Risksammanställning (Risklogg)",
            "Dokument 12 – Arbetsmiljöplan (AMP)",
            "Dokument 25 – Q&A-logg (om tillämpas)",
            "Dokument 10 – Arbetsområdesutredning (om tillämpas)",
          ],
        },
        { key: "bilagor", label: "Övriga bilagor", type: "textarea" },
        {
          key: "bekraftelse",
          label: "Bas-U bekräftar att dokumenten mottagits och genomgåtts",
          type: "textarea",
        },
      ],
    },
    {
      key: "uppfoljningspunkter",
      title: "Uppföljningspunkter för Bas-U",
      intro: ["Bas-P redovisar särskilda frågor som Bas-U behöver följa upp under byggskedet."],
      bullets: [
        "risker som kräver särskilda arbetsberedningar",
        "risker kopplade till tillfälliga konstruktioner",
        "frågor som kräver ytterligare samordning mellan entreprenörer",
        "frågor där projekteringen fortsätter under byggskedet",
      ],
      fields: [{ key: "punkter", label: "Uppföljningspunkter", type: "textarea" }],
    },
    {
      key: "fragor",
      title: "Frågor och övrigt",
      intro: [
        "Bas-U ges möjlighet att ställa frågor och begära förtydliganden kring risker i projekteringen, arbetsmiljöplanens innehåll, arbetsmiljöansvar i byggskedet och eventuella oklarheter i underlagen.",
      ],
      fields: [{ key: "anteckningar", label: "Frågor och svar vid mötet", type: "textarea" }],
    },
    {
      key: "bekraftelse",
      title: "Bekräftelse av överlämning",
      intro: [
        "Genom underskrift bekräftar parterna att överlämning av arbetsmiljöarbetet mellan Bas-P och Bas-U har genomförts.",
        "Bas-U bekräftar att mottagna dokument och identifierade risker är kända inför byggskedet.",
      ],
      fields: [
        {
          key: "signaturer",
          label: "Underskrifter",
          type: "signature",
          parties: ["Bas-P", "Bas-U", "Projektledare / Byggherre"],
        },
      ],
    },
  ],
  byggherre: [
    {
      key: "huvud",
      title: "Dokumenthuvud",
      fields: [
        { key: "projektnummer", label: "Projektnummer" },
        { key: "projektnamn", label: "Projektnamn" },
        { key: "version", label: "Version" },
        { key: "status", label: "Status" },
      ],
    },
    {
      key: "bakgrund",
      title: "Bakgrund",
      intro: [
        "Byggherren ansvarar för att arbetsmiljödokumentation som kan behövas vid framtida arbeten med byggnaden eller anläggningen finns, är anpassad till ändringar under byggskedet och är färdigställd vid projektets avslut.",
        "Arbetsmiljödokumentationen ska upprättas och överlämnas i enlighet med AFS 2023:3 kapitel 10, som reglerar dokumentation för framtida arbeten såsom drift, underhåll, reparation, ändring och rivning.",
        "Byggherren ska även förvara dokumentationen så länge objektet består och överlämna den vid överlåtelse till ny innehavare.",
      ],
      fields: [{ key: "kommentar", label: "Projektspecifik kommentar", type: "textarea" }],
    },
    {
      key: "syfte",
      title: "Syfte",
      intro: ["Syftet med Dokument 22 är att:"],
      bullets: [
        "skapa en formell och spårbar slutöverlämning till byggherren",
        "säkerställa att Dokument 02 (Arbetsmiljödokumentation) är komplett, verifierad och arkiverad",
        "säkerställa att relevanta underlag som påverkar framtida säkra arbeten i bruksskedet är identifierade och kopplade",
        "tydliggöra ägarskap, lagring och tillgänglighet i förvaltningsskedet",
      ],
      fields: [],
    },
    {
      key: "omfattning",
      title: "Omfattning",
      intro: ["Dokument 22 används vid:"],
      bullets: [
        "projektavslut (när byggnads- och anläggningsarbetena avslutas)",
        "delöverlämningar eller etappöverlämningar om byggherren kräver detta",
        "större byten av förvaltningsorganisation eller vid avtalskrav på slutleveranspaket",
      ],
      fields: [
        {
          key: "omfattning",
          label: "Omfattning i detta projekt",
          hint: "Om projektdata inte är känd ska fält markeras som [ospecificerat] tills slutdata finns.",
          type: "textarea",
        },
      ],
    },
    {
      key: "roller",
      title: "Roller och ansvar",
      intro: [
        "Bas-P: säkerställer att överlämningspaketet är sammanställt och spårbart samt att bruksrisker och riskreducerande förutsättningar är införda i Dokument 02.",
        "Bas-U: verifierar att Dokument 02 stämmer med slutligt utförande och att ändringar under byggskedet är införda, samt att relevanta underlag från entreprenörer och leverantörer är levererade.",
        "Byggherren: mottar, fastställer och arkiverar Dokument 02, säkerställer långtidsförvaring och rutin för överlåtelse vid ägarbyte samt att förvaltningsorganisationen vet var slutversionen finns.",
      ],
      fields: [
        { key: "basp", label: "Bas-P – namn och företag" },
        { key: "basu", label: "Bas-U – namn och företag" },
        { key: "byggherre", label: "Byggherre / förvaltning – namn och företag" },
      ],
    },
    {
      key: "process",
      title: "Överlämningsprocess – steg för steg",
      fields: [
        {
          key: "steg1",
          label: "Steg 1 – Förberedelser",
          hint: "Bas-P tar fram slutpaketlistan. Bas-U säkerställer att relationsunderlag och ändringslogg är levererade till dokumentytan.",
          type: "textarea",
        },
        {
          key: "steg2",
          label: "Steg 2 – Slutkontroll",
          hint: "Dokument 02 komplett, Dokument 21 ifyllt och avstämt, Dokument 11 stängd eller markerad med kvarstående risker. Bas-U kontrollerar att Dokument 02 stämmer med slutligt utförande.",
          type: "textarea",
        },
        {
          key: "steg3",
          label: "Steg 3 – Överlämningsmöte",
          hint: "Var Dokument 02 arkiveras och hur det nås, viktigaste kvarstående risker för drift och underhåll, samt hur framtida ändringar hanteras.",
          type: "textarea",
        },
        {
          key: "steg4",
          label: "Steg 4 – Arkivering",
          hint: "Byggherren bekräftar arkivering och anger arkivreferens. Samma referens ska återfinnas i Dokument 00.",
          type: "textarea",
        },
        { key: "arkivsystem", label: "Arkivsystem" },
        { key: "arkivreferens", label: "Arkivreferens / länk" },
      ],
    },
    {
      key: "leverans_a",
      title: "Leveranschecklista A – Obligatoriska leverabler",
      fields: [
        {
          key: "checklista",
          label: "Obligatoriska leverabler",
          type: "checklist",
          items: [
            "Dokument 02 – Arbetsmiljödokumentation, slutversion, daterad",
            "Relationsunderlag som Dokument 02 bygger på (ritningar, systemöversikter, produktdata)",
            "Lista över använda material eller byggprodukter där relevant för framtida säkert arbete",
          ],
        },
        { key: "kommentar", label: "Kommentar", type: "textarea" },
      ],
    },
    {
      key: "leverans_b",
      title: "Leveranschecklista B – Underlag som ska vara kopplade / spårbara",
      fields: [
        {
          key: "checklista",
          label: "Kopplade underlag",
          type: "checklist",
          items: [
            "Dokument 21 – Bruksrisk-checklista komplett",
            "Dokument 11 – Risklogg stängd eller överlämnad",
            "Dokument 25 – Q&A-logg stängd och verifierad mot Dokument 02",
            "Dokument 04 – Uppföljning arbetsplatsens utformning verifierad",
          ],
        },
        { key: "kommentar", label: "Kommentar", type: "textarea" },
      ],
    },
    {
      key: "leverans_c",
      title: "Leveranschecklista C – Projektarkiv",
      fields: [
        {
          key: "checklista",
          label: "Projektarkiv",
          type: "checklist",
          items: [
            "Dokument 12 – Arbetsmiljöplan, slutversion arkiverad",
            "Dokument 31 – Register för arbetsberedningar och arbetstillstånd",
            "Övriga relevanta protokoll eller beslut som påverkar bruksskedet",
          ],
        },
        { key: "kommentar", label: "Kommentar", type: "textarea" },
      ],
    },
    {
      key: "signatur",
      title: "Underskrifter",
      fields: [
        {
          key: "signaturer",
          label: "Underskrifter",
          type: "signature",
          parties: ["Bas-P", "Bas-U", "Byggherre"],
        },
      ],
    },
  ],
  slutsammanfattning: [
    {
      key: "huvud",
      title: "Dokumenthuvud",
      fields: [
        { key: "projektnummer", label: "Projektnummer" },
        { key: "projektnamn", label: "Projektnamn" },
        { key: "version", label: "Version" },
        { key: "status", label: "Status" },
      ],
    },
    {
      key: "parter",
      title: "Projekt och parter",
      intro: [
        "Sammanfattning som Bas-P upprättar när projekteringen är avslutad och arbetsmiljödokumentationen ska lämnas över inför bruksskedet.",
      ],
      fields: [
        { key: "objekt", label: "Objekt / byggnadsverk" },
        { key: "datum", label: "Datum för överlämning", type: "date" },
        { key: "basp", label: "Bas-P" },
        { key: "basu", label: "Bas-U" },
        { key: "byggherre", label: "Byggherre / förvaltning" },
      ],
    },
    {
      key: "handlingar",
      title: "Överlämnade handlingar",
      intro: ["Bocka av de handlingar som ingår i överlämningen och kommentera avvikelser."],
      fields: [
        {
          key: "checklista",
          label: "Handlingar som lämnas över",
          type: "checklist",
          items: [
            "Arbetsmiljöplan (Dok 12)",
            "Risksammanställning utförande (Dok 30b)",
            "Kvarvarande bruksrisker (Dok 31)",
            "Arbetsområdesutredning (Dok 10)",
            "Skydds- och ordningsregler (Dok 15)",
            "Uppföljning bruksskede – arbetsplatsens utformning (Dok 33)",
            "Q&A-logg / ändringar och avsteg (Dok 25)",
          ],
        },
        { key: "kommentar", label: "Kommentar till handlingarna", type: "textarea" },
      ],
    },
    {
      key: "statuslage",
      title: "Sammanfattning av statusläget",
      intro: ["Siffrorna hämtas automatiskt från projektet när dokumentet skapas och kan uppdateras."],
      fields: [
        { key: "bruksrisker", label: "Kvarvarande bruksrisker (Dok 31)", type: "textarea" },
        { key: "uppfoljning", label: "Uppföljning bruksskede (Dok 33)", type: "textarea" },
        { key: "qalogg", label: "Öppna frågor i Q&A-loggen (Dok 25)", type: "textarea" },
        { key: "amp", label: "Status arbetsmiljöplan", type: "textarea" },
      ],
    },
    {
      key: "kvarstaende",
      title: "Kvarstående risker och åtgärder till drift och förvaltning",
      intro: ["Punkter som förs vidare från Dok 31 och Dok 33, med ansvarig och datum."],
      fields: [
        { key: "risker", label: "Kvarstående risker", type: "textarea" },
        { key: "atgarder", label: "Planerade åtgärder, ansvarig och datum", type: "textarea" },
        { key: "drift", label: "Drift- och underhållsanvisningar", type: "textarea" },
      ],
    },
    {
      key: "skyddsrond",
      title: "Underlag inför överlämnande skyddsrond",
      intro: [
        "Bas-U genomför den avslutande skyddsronden med entreprenören. Här samlas det som ska kontrolleras på plats.",
      ],
      fields: [
        { key: "punkter", label: "Punkter att kontrollera på plats", type: "textarea" },
        { key: "datum", label: "Planerat datum för skyddsrond", type: "date" },
        { key: "deltagare", label: "Deltagare", type: "textarea" },
        { key: "genomford", label: "Kvittens – skyddsrond genomförd (datum och resultat)", type: "textarea" },
      ],
    },
    {
      key: "signaturer",
      title: "Underskrifter",
      fields: [
        {
          key: "signaturer",
          label: "Underskrifter",
          type: "signature",
          parties: ["Bas-P", "Bas-U", "Byggherre / förvaltning"],
        },
      ],
    },
  ],
  utseende_basp: buildAppointmentSections("Bas-P"),
  utseende_basu: buildAppointmentSections("Bas-U"),
};


export type HandoverStatus = "draft" | "signed" | "acknowledged";

export const HANDOVER_STATUS_LABELS: Record<HandoverStatus, string> = {
  draft: "Utkast",
  signed: "Överlämnad",
  acknowledged: "Kvitterad av mottagare",
};

// ---------- Dok 25: Q&A-logg / Ändringar och avsteg ----------

/** Dokumenttitel exakt enligt mall 25 */
export const QA_DOC_TITLE = "Ändringar och avsteg under planering och projekteringen";
// Följer mallen "25 MALL Q&A Logg" – ändringar och avsteg under planering och projektering.

export const QA_INTRO_SECTIONS: { title: string; paragraphs: string[]; bullets?: string[] }[] = [
  {
    title: "Syfte",
    paragraphs: [
      "Detta avsnitt redovisar ändringar och avsteg från tekniska anvisningar, standarder eller tidigare kravställning som uppstår under planering och projektering och som kan ha betydelse för arbetsmiljö, säkerhet, drift eller underhåll.",
      "Syftet är att:",
    ],
    bullets: [
      "säkerställa spårbarhet mellan projekteringsbeslut och arbetsmiljökonsekvenser",
      "dokumentera arbetsmiljömässiga motiv till tekniska eller organisatoriska avsteg",
      "tydliggöra ansvar och koppling till gällande arbetsmiljöregelverk",
      "utgöra underlag till den arbetsmiljödokumentation som ska upprättas enligt AFS 2023:3",
    ],
  },
  {
    title: "Omfattning",
    paragraphs: ["Sammanställningen omfattar beslut under planering och projektering som innebär:"],
    bullets: [
      "avsteg från tekniska anvisningar eller standarder",
      "förändrade krav eller lösningar i projekteringen",
      "metodval som påverkar arbetsmiljö under byggskede eller bruksskede",
      "gränsdragningar mellan projekt, entreprenör och framtida driftorganisation",
    ],
  },
  {
    title: "Hur tabellen ska läsas",
    paragraphs: [
      "Varje rad i tabellen motsvarar ett beslut eller kravärende som har hanterats under planering eller projektering. Endast beslut som bedöms ha arbetsmiljömässig relevans redovisas.",
    ],
  },
];

export const QA_COLUMNS: { key: string; label: string; hint: string; multiline?: boolean }[] = [
  { key: "subject", label: "Ämne / fråga", hint: "Kort beskrivning av det aktuella ärendet eller frågeställningen.", multiline: true },
  { key: "decision_ref", label: "Beslutsreferens / ärende-ID", hint: "Intern referens som gör beslutet spårbart i projektets dokumentation." },
  { key: "decision_summary", label: "Kortsammanfattning / beslut", hint: "Vilket beslut som fattats eller vilken lösning som valts.", multiline: true },
  { key: "change_description", label: "Vad har ändrats / hur hanteras det", hint: "Den tekniska eller organisatoriska förändringen.", multiline: true },
  { key: "responsible_function", label: "Ansvarig funktion", hint: "Den funktion eller roll som ansvarar för kravet eller beslutet." },
  { key: "afs_reference", label: "AFS-referens", hint: "Koppling till relevant arbetsmiljöregelverk eller föreskrift." },
  { key: "comment", label: "Kommentar", hint: "Arbetsmiljömässig bedömning, motivering eller gränsdragning.", multiline: true },
];

export type QaStatus = "open" | "decided" | "closed";

export const QA_STATUS_LABELS: Record<QaStatus, string> = {
  open: "Öppen",
  decided: "Beslutad",
  closed: "Stängd",
};

export const QA_ROLES = ["Byggherre", "Projektledare", "Bas-P", "Bas-U", "Projektör", "Entreprenör"] as const;

// ---------- Dok 28: Tillbud- och olycksrapportering ----------

export type IncidentTypeValue = "tillbud" | "olycka" | "allvarligt_tillbud" | "allvarlig_olycka";
export type IncidentStatusValue = "draft" | "under_investigation" | "reported" | "closed";

export const INCIDENT_TYPE_LABELS: Record<IncidentTypeValue, string> = {
  tillbud: "Tillbud",
  olycka: "Olycka",
  allvarligt_tillbud: "Allvarligt tillbud",
  allvarlig_olycka: "Allvarlig olycka",
};

export const INCIDENT_STATUS_LABELS: Record<IncidentStatusValue, string> = {
  draft: "Utkast",
  under_investigation: "Under utredning",
  reported: "Anmäld till AMV",
  closed: "Avslutad",
};

export const INCIDENT_AMV_HINT =
  "Allvarlig olycka och allvarligt tillbud ska anmälas till Arbetsmiljöverket utan dröjsmål enligt AFS 2023:1.";

// ---------- Dok 27: Skyddsrondsprotokoll ----------

export type SafetyRoundType = "ordinarie" | "overlamnande";

export const SAFETY_ROUND_TYPE_LABELS: Record<SafetyRoundType, string> = {
  ordinarie: "Ordinarie skyddsrond",
  overlamnande: "Överlämnande skyddsrond",
};

export type SafetyRoundStatus = "unanswered" | "ok" | "deviation" | "action_planned" | "not_applicable";

export const SAFETY_ROUND_STATUS_LABELS: Record<SafetyRoundStatus, string> = {
  unanswered: "Ej kontrollerad",
  ok: "Utan anmärkning",
  deviation: "Avvikelse",
  action_planned: "Åtgärd planerad",
  not_applicable: "Ej aktuell",
};

export interface SafetyRoundItemDef {
  key: string;
  controlPoint: string;
  legalReference?: string;
  explanation?: string;
}

export interface SafetyRoundSectionDef {
  section: string;
  items: SafetyRoundItemDef[];
}

/**
 * Mall för ordinarie skyddsrond på arbetsplatsen – 39 kontrollpunkter
 * enligt BAMS skyddsrondsprotokoll, grupperade per kontrollområde.
 */
export const SAFETY_ROUND_TEMPLATE: SafetyRoundSectionDef[] = [
  {
    section: "1. Etablering, ordning och personalutrymmen",
    items: [
      { key: "etab-1", controlPoint: "Är arbetsplatsen inhägnad och obehöriga hindrade från tillträde?", legalReference: "AFS 2023:3", explanation: "Grindar, staket och skyltning ska hindra obehöriga och tredje man." },
      { key: "etab-2", controlPoint: "Är arbetsplatsen ordnad, städad och fri från snubbel- och spikrisker?", legalReference: "AFS 2023:12" },
      { key: "etab-3", controlPoint: "Är transport-, gång- och utrymningsvägar fria och tydligt markerade?", legalReference: "AFS 2023:12" },
      { key: "etab-4", controlPoint: "Är belysningen tillräcklig i arbets-, transport- och utrymningsvägar?", legalReference: "AFS 2023:12" },
      { key: "etab-5", controlPoint: "Är personalutrymmen, omklädning, tvätt och toalett i användbart skick?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    section: "2. Fallrisk och arbete på höjd",
    items: [
      { key: "fall-1", controlPoint: "Är skyddsräcken hela, kompletta och rätt monterade vid alla fallkanter?", legalReference: "AFS 2023:11" },
      { key: "fall-2", controlPoint: "Är håltagningar, schakt och öppningar täckta, låsta och skyltade?", legalReference: "AFS 2023:11" },
      { key: "fall-3", controlPoint: "Är ställningar besiktigade, skyltade (grön/röd skylt) och kompletta?", legalReference: "AFS 2023:11" },
      { key: "fall-4", controlPoint: "Används stegar, arbetsbockar och plattformar endast till avsett arbete?", legalReference: "AFS 2023:11" },
      { key: "fall-5", controlPoint: "Används personlig fallskyddsutrustning med godkända förankringspunkter?", legalReference: "AFS 2023:11" },
      { key: "fall-6", controlPoint: "Finns rutin och beredskap för räddning vid hängande i sele?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "3. Maskiner, verktyg och lyft",
    items: [
      { key: "mask-1", controlPoint: "Är maskiner hela, CE-märkta och med skydd på plats?", legalReference: "AFS 2023:11" },
      { key: "mask-2", controlPoint: "Genomförs daglig tillsyn av maskiner och handverktyg före användning?", legalReference: "AFS 2023:11" },
      { key: "mask-3", controlPoint: "Finns giltiga besiktningar för lyftanordningar och lyftredskap?", legalReference: "AFS 2023:11" },
      { key: "mask-4", controlPoint: "Har förare och lyftande personal dokumenterad behörighet och körtillstånd?", legalReference: "AFS 2023:11" },
      { key: "mask-5", controlPoint: "Är lyftområden avspärrade så ingen vistas under hängande last?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "4. Trafik, fordon och tredje man",
    items: [
      { key: "traf-1", controlPoint: "Är gående separerade från fordons- och maskintrafik?", legalReference: "AFS 2023:3" },
      { key: "traf-2", controlPoint: "Följs trafikanordningsplanen (TA-plan) på och vid arbetsplatsen?", legalReference: "AFS 2023:3" },
      { key: "traf-4", controlPoint: "Är tredje man skyddad från fallande föremål, damm och buller?", legalReference: "AFS 2023:3" },
    ],
  },
  {
    section: "5. El, brand och heta arbeten",
    items: [
      { key: "el-1", controlPoint: "Är tillfällig el hel, jordfelsbrytad och fri från skadade kablar?", legalReference: "ELSÄK-FS 2022:1" },
      { key: "el-2", controlPoint: "Är befintliga el- och kraftledningar identifierade, spänningslösa eller skyddade?", legalReference: "ELSÄK-FS 2022:1" },
      { key: "el-3", controlPoint: "Finns giltiga tillstånd för heta arbeten med brandvakt och släckutrustning?", legalReference: "AFS 2023:11" },
      { key: "el-4", controlPoint: "Är brandsläckare, larm och utrymningsplan kontrollerade och kända?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    section: "6. Kemiska och fysikaliska risker",
    items: [
      { key: "kem-1", controlPoint: "Finns aktuell kemikalieförteckning och säkerhetsdatablad tillgängliga på plats?", legalReference: "AFS 2023:10" },
      { key: "kem-2", controlPoint: "Är riskbedömning gjord för de kemiska produkter som används?", legalReference: "AFS 2023:10" },
      { key: "kem-3", controlPoint: "Hanteras asbest, PCB och annat farligt material enligt tillstånd och rutin?", legalReference: "AFS 2023:10" },
      { key: "kem-4", controlPoint: "Begränsas kvarts- och byggdamm med utsug, vattenbegjutning eller inbyggnad?", legalReference: "AFS 2023:10" },
      { key: "kem-5", controlPoint: "Är buller och vibrationer begränsade och skyddsutrustning tillgänglig?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "7. Schakt, stomme och tillfälliga konstruktioner",
    items: [
      { key: "kons-1", controlPoint: "Är schakt släntade, spontade eller stämpade enligt handling?", legalReference: "AFS 2023:13" },
      { key: "kons-2", controlPoint: "Är ledningar i mark identifierade och utsatta före grävning?", legalReference: "AFS 2023:13" },
      { key: "kons-3", controlPoint: "Är tillfälliga och samverkande konstruktioner kontrollerade av kompetent person (Dok 44)?", legalReference: "AFS 2023:13" },
    ],
  },
  {
    section: "8. Personlig skyddsutrustning, kompetens och beteende",
    items: [
      { key: "psu-1", controlPoint: "Används föreskriven skyddsutrustning av alla på arbetsplatsen?", legalReference: "AFS 2023:11" },
      { key: "psu-2", controlPoint: "Har alla på plats fått arbetsmiljöintroduktion och är registrerade?", legalReference: "AFS 2023:3" },
      { key: "psu-3", controlPoint: "Är skydds- och ordningsreglerna (Dok 15) kända och efterlevda?", legalReference: "AFS 2023:3" },
      { key: "psu-4", controlPoint: "Är arbetsberedningar (Dok 24) genomgångna med de som utför arbetet?", legalReference: "AFS 2023:3" },
      { key: "psu-5", controlPoint: "Finns dokumenterad kompetens och behörighet för riskfyllda arbeten?", legalReference: "AFS 2023:11" },
    ],
  },
  {
    section: "9. Uppföljning, beredskap och dokumentation",
    items: [
      { key: "upp-1", controlPoint: "Är avvikelser från föregående skyddsrond åtgärdade och avslutade?", legalReference: "AFS 2023:1" },
      { key: "upp-2", controlPoint: "Är rapporterade tillbud och olyckor (Dok 28) utredda och åtgärdade?", legalReference: "AFS 2023:1" },
      { key: "upp-3", controlPoint: "Är arbetsmiljöplanen (Dok 12) uppdaterad och tillgänglig på arbetsplatsen?", legalReference: "AFS 2023:3" },
    ],
  },
];

/** Mall för den överlämnande skyddsronden inför bruksskedet */
export const HANDOVER_SAFETY_ROUND_TEMPLATE: SafetyRoundSectionDef[] = [
  {
    section: "Färdigställande och avetablering",
    items: [
      { key: "avs-1", controlPoint: "Är byggetablering, avspärrningar och tillfälliga skydd avvecklade?", legalReference: "AFS 2023:3" },
      { key: "avs-2", controlPoint: "Är arbetsområdet städat och överlämningsbart?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    section: "Permanenta skyddsanordningar",
    items: [
      { key: "perm-1", controlPoint: "Är permanenta räcken, taksäkerhet och fästpunkter monterade och besiktigade?", legalReference: "AFS 2023:11" },
      { key: "perm-2", controlPoint: "Är tillträdesvägar till drift- och underhållsutrymmen säkra och godkända?", legalReference: "AFS 2023:12" },
      { key: "perm-3", controlPoint: "Är utrymningsvägar, skyltning och nödbelysning kontrollerade?", legalReference: "AFS 2023:12" },
    ],
  },
  {
    section: "Kvarstående risker och dokumentation",
    items: [
      { key: "kvar-1", controlPoint: "Är kvarvarande bruksrisker (Dok 31) genomgångna på plats?", legalReference: "AFS 2023:11" },
      { key: "kvar-2", controlPoint: "Är avvikelser från Dok 33 kontrollerade i verkligheten?", legalReference: "AFS 2023:3" },
      { key: "kvar-3", controlPoint: "Är drift- och underhållsinstruktioner överlämnade till förvaltningen?", legalReference: "AFS 2023:11" },
    ],
  },
];
